import queryString from 'query-string';

import {
	UPDATE_FEES_AND_LIMITS,
	GENERAL_ERROR,
	VALIDATE_SPEC,
	PROMPT_SECUREID,
	EXECUTE_DEPOSIT_PRECHECK
} from '@comeon/actions-public';
import { createUrlParameters } from '@comeon/utility';
import { requestApi } from '~/services/api.js';
import formatAmount from '../deposit/data/format-amount.js';
import checkAmountLimits from '../deposit/data/check-amount-limits.js';
import formatDefaultDepositAmounts from '../deposit/data/format-default-deposit-amounts.js';
import { hasCreditcardFormatOfCardType } from '../deposit/data/creditcard-formats.js';
import { stringToNumber } from '../deposit/data/string-num-clean.js';
import encryptStringWithRsaPublicKey from '../util/encrypt-util.js';
import {
	amount as validateAmount,
	dotpayBlik,
	creditCard,
	emailCurrencies,
	onlyCurrencies,
	mobileNumber,
	neteller,
	ecobanq,
	swish,
	bankWireIndia,
	ezeewallet,
	paymeroUpi,
	paymeroBanking,
	validateCardNumberbyProvider,
	validateCardNumberbyAvailability,
	venuspoint,
	akontoPayUpi
} from '../deposit/data/validate';
import { selectIsGermanRegulatedFranchise } from '~/components/brand/ducks';
import { selectBalance, selectRemainingGermanyBonusValue } from '~/components/account/ducks';
import { formatBonusValue } from '~/components/payment/deposit/data/formatBonusValue';
import { limitsConst } from '~/components/payment/common/lib/utils/constants';
import { FREEBET, OPT_IN } from '~/constants';
import { allowsMethod } from '~/components/bonus/ducks/public';

const NO_BONUS_ID_TYPES = [FREEBET, OPT_IN];

function cancelPrompts() {
	return dispatch => {
		dispatch({
			type: PROMPT_SECUREID,
			payload: null
		});
	};
}

function updateExecutePrecheck(payload) {
	return dispatch => {
		dispatch({
			type: EXECUTE_DEPOSIT_PRECHECK,
			payload
		});
	};
}

function showPrompts(payload) {
	return dispatch => {
		dispatch({
			type: PROMPT_SECUREID,
			payload
		});
	};
}

function generalError(payload) {
	return dispatch => {
		dispatch({
			type: GENERAL_ERROR,
			payload: { confirmed: false, payload }
		});
	};
}

function getPAGdata(location) {
	const URLqueryParams = new URLSearchParams(location).get('sidebar');
	return URLqueryParams?.includes('pag') && URLqueryParams.split('pag=').pop();
}

function getBrowserInfo() {
	return {
		language: navigator.language,
		timeZoneId: Intl.DateTimeFormat().resolvedOptions().timeZone,
		width: window.screen.width,
		height: window.screen.height,
		colorDepth: window.screen.colorDepth,
		pixelDepth: window.screen.pixelDepth,
		pixelRatio: window.devicePixelRatio || 1,
		viewportHeight: document.documentElement?.clientHeight,
		viewportWidth: document.documentElement?.clientWidth,
		javascriptEnabled: true
	};
}

async function setLimits(depositLimits) {
	const url = '/player/limit/updateDepositLimit';
	let payload = {
		freq: 'month',
		amount: ''
	};
	depositLimits.forEach(depositLimit => {
		switch (depositLimit.freq) {
			case 'month':
				payload = { ...payload, amount: depositLimit.amount };
				break;
			default:
				break;
		}
	});
	const response = await requestApi(url, payload, 'POST');
	return response;
}
async function selectProduct(isCasinoAndSBProduct) {
	const payload = {
		hasSelectedProductInDepositFlow: true,
		casinoAndSportsBookProduct: isCasinoAndSBProduct,
		hasSelectedProduct: true
	};
	const response = requestApi('/player/productSelection', payload, 'POST');
	return response;
}

/**
 * wrappedRequestApi: To be able to catch failing ajax-requests the requestApi is wrapped into this
 * try-catch. It requires the requestApi to throw an error for a failed request (http:404, http:500)
 * @param {endpoint:url, payload:data-arguments, urlencodedContentType: boolean, netellerHeaders: boolean} param0
 * @param {*} dispatch
 */
async function wrappedRequestApi({ endpoint, payload, method, urlencodedContentType, netellerHeaders }, dispatch) {
	try {
		const response = await requestApi(endpoint, payload, method, urlencodedContentType, netellerHeaders);
		return response;
	} catch (err) {
		/* catch a server error */
		// eslint-disable-next-line no-console
		console.warn('payment: wrappedRequestApi: connection error', err);
		return dispatch(generalError('connection'));
	}
}

function createBonusParams(data) {
	return {
		...(data.bonusId ? { bonusId: data.bonusId, bid: data.bonusId } : {}),
		...(data.bonusAddOnId
			? {
					bonusAddOnId: data.bonusAddOnId,
					baid: data.bonusAddOnId
				}
			: {}),
		...(data.bonusAddOnOptionsId
			? {
					bonusAddOnOptionsId: data.bonusAddOnOptionsId,
					oid: data.bonusAddOnOptionsId
				}
			: {})
	};
}

async function getCreditCardEncryptionKey(dispatch) {
	const endpoint = '/CreditCardIntegration/getCreditCardKey';
	return wrappedRequestApi({ endpoint }, dispatch);
}

async function encryptCreditCardNumber(creditCardNumber, dispatch) {
	let encryptedCreditCardNumber = creditCardNumber;
	if (encryptedCreditCardNumber && encryptedCreditCardNumber !== '') {
		let publicKey = null;
		await getCreditCardEncryptionKey(dispatch).then(creditCardEncryptResponse => {
			publicKey = creditCardEncryptResponse.result;
		});
		if (publicKey && publicKey !== '') {
			encryptedCreditCardNumber = await encryptStringWithRsaPublicKey(creditCardNumber, publicKey);
		}
	}
	return encryptedCreditCardNumber;
}

function getFeesAndLimits(amount) {
	/* the amount is passed as a parameter, since the state returns inaccurate
	value when this method is called from 'updateAmount' */
	/* for creditcard the getFeesAndLimitsUrlCreditCard() must have completed */
	return async (dispatch, getState) => {
		const { defaultDepositAmountsFormatted: currentDefaults = {}, amlLimits = {} } = getState().payment;
		const previousSelect = currentDefaults.selected || null;
		dispatch({
			type: UPDATE_FEES_AND_LIMITS,
			payload: {
				response: null,
				summary: {
					sum_pending: true,
					sum_balance: null,
					sum_bonus: null,
					sum_deposit: null,
					sum_debit: null,
					sum_fee: null,
					sum_above: null,
					sum_aboveIsZero: null,
					sum_below: null,
					sum_within: null,
					sum_max: null,
					sum_max_rg_limit: null,
					sum_min: null
				},
				defaultDepositAmountsFormatted: currentDefaults
			}
		});
		const { am: specAm, cu } = getState().payment.spec;
		const am = amount || validateAmount.withDecimals.clean(specAm);
		const defaultUrl = '/PaymentFormDataService/getDepositFeesAndLimits/forMobile';
		const {
			shopItem: { type },
			status: { methodId, formTemplateCode, selectedMethodDetails },
			spec: { cn, cid, bonusId }
		} = getState().payment;
		const mId = parseInt(methodId, 10);
		let url = defaultUrl;
		let urlencodedContentType = true;
		const { allowedForMethod } = getState().bonuses;
		let params = {
			paymentMethodId: mId,
			creditCardNumber: cn,
			playerCardId: cid,
			amount: am,
			isMobileDevice: true,
			isInstantDeposit: false,
			currency: cu,
			bonusId: allowedForMethod && !NO_BONUS_ID_TYPES.includes(type) && bonusId ? bonusId : ''
		};
		let payload = queryString.stringify(params);
		const zeroAmount = am === '0' || am === 0;

		if (formTemplateCode === 'CREDIT-CARD') {
			const { ccLimitUrl = null } = getState().payment;
			url = ccLimitUrl;

			const encryptedCc = await encryptCreditCardNumber(cn, dispatch);
			params = { ...params, creditCardNumber: encryptedCc };
			payload = params;
			urlencodedContentType = false;
		}
		if (formTemplateCode === 'DIRECTLY-ROUTED-CREDIT-CARD') {
			url =
				'/CreditCardIntegration/client/rest/DirectlyRoutedCreditCard/getDirectlyRoutedCreditCardFeesAndLimits';

			const encryptedCc = await encryptCreditCardNumber(cn, dispatch);
			params = { ...params, creditCardNumber: encryptedCc };
			payload = params;
			urlencodedContentType = false;
		}

		if (formTemplateCode === 'ASTROPAY-CREDIT-CARD') {
			const encryptedCc = await encryptCreditCardNumber(cn, dispatch);
			params = { ...params, creditCardNumber: encryptedCc };
			payload = queryString.stringify(params);
		}

		const response =
			zeroAmount && !selectedMethodDetails?.isAmountRestricted
				? await null
				: await wrappedRequestApi(
						{
							endpoint: url,
							payload,
							method: 'POST',
							urlencodedContentType
						},
						dispatch
					);
		const {
			depositLimitData: {
				PARAM_CURRENCY_ISO,
				PARAM_PLAYER_CURRENCY,
				PARAM_MAX_OPERATION,
				PARAM_MIN_OPERATION,
				PARAM_SCHUFA_CONSENT_NEEDED,
				PARAM_SCHUFA_MONTHLY_REMAINING_AMT,
				PARAM_RESPONSIBLE_GAMING_MAX_DEPOSIT,
				PARAM_RESPONSIBLE_GAMING_LIMIT,
				responsibleGamingLimitType
			} = {
				PARAM_CURRENCY_ISO: null,
				PARAM_PLAYER_CURRENCY: null,
				PARAM_MAX_OPERATION: null,
				PARAM_MIN_OPERATION: null,
				PARAM_SCHUFA_CONSENT_NEEDED: null,
				PARAM_SCHUFA_MONTHLY_REMAINING_AMT: null,
				PARAM_RESPONSIBLE_GAMING_MAX_DEPOSIT: null,
				PARAM_RESPONSIBLE_GAMING_LIMIT: null,
				responsibleGamingLimitType: limitsConst.DEFAULT
			},
			defaultDepositAmounts
		} = { ...response };
		// if the method is Siru, a list of default-deposit-amounts is created
		const defaultDepositAmountsFormatted =
			response && mId === 47 ? formatDefaultDepositAmounts(defaultDepositAmounts, PARAM_CURRENCY_ISO) : {};

		defaultDepositAmountsFormatted.selected = previousSelect || 'PARAM_AMOUNT1';
		const {
			depositFeeAmounts: {
				PARAM_FEE_AMOUNT,
				PARAM_FEE_AMOUNT_VALUE,
				PARAM_TOTAL_AMOUNT,
				PARAM_TOTAL_AMOUNT_VALUE
			} = {
				PARAM_FEE_AMOUNT: null,
				PARAM_FEE_AMOUNT_VALUE: null,
				PARAM_TOTAL_AMOUNT: null,
				PARAM_TOTAL_AMOUNT_VALUE: null
			}
		} = { ...response };
		const {
			bonusAmountsData: { bonusWasFound, PARAM_BONUS_AMOUNT_VALUE, bonusCurrencyCode } = {
				bonusWasFound: null,
				PARAM_BONUS_AMOUNT: null
			}
		} = { ...response };
		const { newBalance = null } = { ...response };
		const remainingBonusValue = selectRemainingGermanyBonusValue(getState());
		let formattedBonus;
		let formattedNewBalance;

		if (selectIsGermanRegulatedFranchise(getState()) && remainingBonusValue !== null && bonusWasFound) {
			const userBalance = selectBalance(getState());
			const bonusValue = formatBonusValue(PARAM_BONUS_AMOUNT_VALUE, remainingBonusValue);

			formattedBonus = formatAmount(bonusValue, PARAM_CURRENCY_ISO);
			formattedNewBalance = formatAmount(
				userBalance + PARAM_TOTAL_AMOUNT_VALUE + bonusValue - PARAM_FEE_AMOUNT_VALUE,
				PARAM_CURRENCY_ISO
			);
		} else {
			formattedBonus = bonusWasFound && formatAmount(PARAM_BONUS_AMOUNT_VALUE, bonusCurrencyCode);
			formattedNewBalance = formatAmount(newBalance, PARAM_PLAYER_CURRENCY);
		}
		const formattedAmount = formatAmount(am, PARAM_CURRENCY_ISO);
		const formattedFee = formatAmount(PARAM_FEE_AMOUNT, PARAM_CURRENCY_ISO);
		const formattedTotal = formatAmount(PARAM_TOTAL_AMOUNT, PARAM_PLAYER_CURRENCY);
		const hasDefinedLimitInfo = !!(Number(am) && PARAM_MAX_OPERATION && PARAM_MIN_OPERATION);
		const limitCheck = hasDefinedLimitInfo
			? checkAmountLimits(
					stringToNumber(am),
					stringToNumber(PARAM_MAX_OPERATION),
					stringToNumber(PARAM_MIN_OPERATION)
				)
			: {
					above: null,
					aboveIsZero: null,
					below: null,
					within: null
				};
		const { above, aboveIsZero, below, within } = limitCheck;
		const block = above || aboveIsZero || below || zeroAmount;
		const blockSign = '-';

		// Only show this rgLimitReached when aml limit is not set
		const isAmlLimit = amlLimits?.enabled && Number(amlLimits.max) === Number(PARAM_MAX_OPERATION);
		const rgLimitReached =
			PARAM_MAX_OPERATION &&
			!isAmlLimit &&
			Number(PARAM_MAX_OPERATION) === Number(PARAM_RESPONSIBLE_GAMING_MAX_DEPOSIT);

		const summary = {
			sum_pending: false,
			sum_balance: block ? blockSign : formattedNewBalance,
			sum_bonus: block ? false : formattedBonus,
			sum_deposit: block ? blockSign : formattedAmount,
			sum_debit: block ? blockSign : formattedTotal,
			sum_fee: block ? blockSign : formattedFee,
			sum_above: above,
			sum_aboveIsZero: aboveIsZero,
			sum_below: zeroAmount || below,
			sum_within: within,
			sum_max: PARAM_MAX_OPERATION,
			rg_limit_reached: rgLimitReached,
			sum_min: PARAM_MIN_OPERATION,
			schufa_consent_form: PARAM_SCHUFA_CONSENT_NEEDED,
			schufa_monthly_remaining: PARAM_SCHUFA_MONTHLY_REMAINING_AMT,
			sum_limit: PARAM_RESPONSIBLE_GAMING_LIMIT,
			responsibleGamingLimitType
		};
		if (response) {
			response.readonly = true;
		}
		return dispatch({
			type: UPDATE_FEES_AND_LIMITS,
			payload: { response, summary, defaultDepositAmountsFormatted }
		});
	};
}

async function validateTemplate(formTemplateCode, spec, blikCodeRequired, methodDetails, paymentMethods) {
	const validCurrency = onlyCurrencies.cu.isAccepted(spec.cu);
	if (formTemplateCode === 'NETELLER') {
		const netellerValidId = neteller.id.isAccepted(spec.id);
		const netellerValidAcc = neteller.externalid.isAccepted(spec.externalid);
		return {
			...spec,
			validSecureId: netellerValidId,
			validAccount: netellerValidAcc,
			validCurrency,
			validForSubmit: Boolean(netellerValidId && netellerValidAcc && validCurrency)
		};
	}
	if (formTemplateCode === 'ECOBANQ') {
		const ecobanqValidAccount = await ecobanq.externalid.isAccepted(spec.externalid);
		const ecobanqValidPassword = ecobanq.pwd.isAccepted(spec.pwd);
		return {
			...spec,
			validAccount: ecobanqValidAccount,
			validPassword: ecobanqValidPassword,
			validCurrency,
			validForSubmit: Boolean(ecobanqValidPassword && ecobanqValidAccount && validCurrency)
		};
	}
	if (formTemplateCode === 'SWISH') {
		const validSwishNumber = await swish.phoneNumber.isAccepted(spec.phoneNumber);
		return {
			...spec,
			validSwishNumber,
			validCurrency,
			validForSubmit: Boolean(validSwishNumber && validCurrency)
		};
	}
	if (formTemplateCode === 'EZEE_WALLET') {
		const ezeewalletValidAccount = await ezeewallet.externalid.isAccepted(spec.externalid);
		const ezeewalletValidPassword = ezeewallet.pwd.isAccepted(spec.pwd);
		return {
			...spec,
			validAccount: ezeewalletValidAccount,
			validPassword: ezeewalletValidPassword,
			validCurrency,
			validForSubmit: Boolean(ezeewalletValidPassword && ezeewalletValidAccount && validCurrency)
		};
	}
	if (formTemplateCode === 'PAYMERO_UPI') {
		const validUpiAccount = await paymeroUpi.externalid.isAccepted(spec.externalid);
		return {
			...spec,
			validAccount: validUpiAccount,
			validCurrency,
			validForSubmit: Boolean(validUpiAccount && validCurrency)
		};
	}
	if (formTemplateCode === 'AKONTO_PAY_UPI') {
		const validUpiAccount = await akontoPayUpi.externalid.isAccepted(spec.externalid);
		return {
			...spec,
			validAccount: validUpiAccount,
			validCurrency,
			validForSubmit: Boolean(validUpiAccount && validCurrency)
		};
	}
	if (
		formTemplateCode === 'PAYMERO_NET_BANKING' ||
		formTemplateCode === 'AKONTO_PAY_NET_BANKING' ||
		formTemplateCode === 'PAYMERO_WALLET' ||
		formTemplateCode === 'ZOTAPAY_NET_BANKING' ||
		formTemplateCode === 'SAFEXPAY_NETBANKING'
	) {
		const validBankCode = await paymeroBanking.bankCode.isAccepted(spec.selectedBank);
		return {
			...spec,
			validAccount: validBankCode,
			validCurrency,
			validForSubmit: Boolean(validBankCode && validCurrency)
		};
	}
	if (formTemplateCode === 'BANK_WIRE_INDIA' || formTemplateCode === 'BANK_WIRE_INDIA_UPI') {
		if (spec.documentId) {
			const validDocumentId = await bankWireIndia.documentId.isAccepted(spec.documentId);
			return {
				...spec,
				validDocumentId,
				validCurrency,
				validForSubmit: Boolean(validDocumentId && validCurrency)
			};
		}
		const validTransactionId = await bankWireIndia.transactionId.isAccepted(spec.transactionId);
		return {
			...spec,
			validTransactionId,
			validCurrency,
			validForSubmit: Boolean(validTransactionId && validCurrency)
		};
	}
	if (
		formTemplateCode === 'CREDIT-CARD' ||
		formTemplateCode === 'DIRECTLY-ROUTED-CREDIT-CARD' ||
		formTemplateCode === 'ASTROPAY-CREDIT-CARD'
	) {
		const paymentMethod = paymentMethods && paymentMethods[spec.paymentMethodId];

		const cc4Type = formTemplateCode === 'ASTROPAY-CREDIT-CARD' || hasCreditcardFormatOfCardType(spec.cn, 'Amex');

		/** Todo: Remove this piece of code for cc4Type and make it as one validation */
		const ccValidId = cc4Type ? creditCard.cc4.isAccepted(spec.cc) : creditCard.cc.isAccepted(spec.cc);

		const ccValidAcc = Boolean(
			(creditCard.cn.isAccepted(spec.cn) &&
				validateCardNumberbyProvider(paymentMethod.methodName, spec.cn) &&
				validateCardNumberbyAvailability(
					paymentMethod.hasMasterCardMethods,
					paymentMethod.hasVisaMethods,
					spec.cn
				)) ||
				creditCard.cid.isAccepted(spec.cid)
		);
		const ccValidDate = Boolean(spec.vm && spec.vy && creditCard.date.isAccepted(spec.vm, spec.vy));
		return {
			...spec,
			utimestamp: new Date().getTime(),
			validSecureId: ccValidId,
			validAccount: ccValidAcc,
			validDate: ccValidDate,
			browserInfo: getBrowserInfo(),
			validForSubmit: ccValidId && ccValidAcc && ccValidDate
		};
	}
	if (formTemplateCode === 'EMAIL-CURRENCIES') {
		const validAccount = await emailCurrencies.em.isAccepted(spec.em);
		return {
			...spec,
			utimestamp: new Date().getTime(),
			validAccount,
			validCurrency,
			validForSubmit: validAccount && validCurrency
		};
	}
	if (formTemplateCode === 'EMAIL') {
		const validAccount = await emailCurrencies.em.isAccepted(spec.em);
		return {
			...spec,
			utimestamp: new Date().getTime(),
			validAccount,
			validForSubmit: validAccount
		};
	}
	if (formTemplateCode === 'ONLY-CURRENCIES') {
		return {
			...spec,
			utimestamp: new Date().getTime(),
			validCurrency,
			validForSubmit: validCurrency
		};
	}
	if (formTemplateCode === 'DOTPAY-BLIK' || formTemplateCode === 'TPAY-BLIK') {
		const validBlikCode = blikCodeRequired ? dotpayBlik.blik.isAccepted(spec.blik) : true;
		return {
			...spec,
			utimestamp: new Date().getTime(),
			validCurrency,
			validForSubmit: validBlikCode
		};
	}
	if (formTemplateCode === 'PHONE') {
		const validCountryCode = mobileNumber.countryCode.isAccepted(spec.countryCode);
		const validPhoneNumber = mobileNumber.number.isAccepted(spec.phoneNumber);
		const validAccount = validCountryCode && validPhoneNumber;
		return {
			...spec,
			utimestamp: new Date().getTime(),
			validAccount,
			validForSubmit: validAccount
		};
	}
	if (formTemplateCode === 'MUCHBETTER') {
		const validCountryCode = mobileNumber.countryCode.isAccepted(spec.countryCode);
		const validPhoneNumber = mobileNumber.number.isAccepted(spec.phoneNumber);
		return {
			...spec,
			utimestamp: new Date().getTime(),
			validCurrency,
			validForSubmit: (methodDetails && methodDetails.hasAccounts) || (validCountryCode && validPhoneNumber)
		};
	}
	if (
		formTemplateCode === 'NO-FIELDS' ||
		formTemplateCode === 'JPAY_DEPOSIT' ||
		formTemplateCode === 'FOGHORN_DEPOSIT' ||
		formTemplateCode === 'CRYPTO_DEPOSIT' ||
		formTemplateCode === 'MUCHBETTER' ||
		formTemplateCode === 'PHONE' ||
		formTemplateCode === 'VIRTUAL-CREDIT-CARD' ||
		formTemplateCode === 'PEER-TO-PEER' ||
		formTemplateCode === 'TSI' ||
		formTemplateCode === 'VOUCHER' ||
		formTemplateCode === 'EMP_CASHLIB' ||
		formTemplateCode === 'IMOJE_BANK'
	) {
		return {
			...spec,
			utimestamp: new Date().getTime(),
			validCurrency,
			validForSubmit: validCurrency
		};
	}
	if (formTemplateCode === 'VENUSPOINT_DEPOSIT') {
		const validAccount = await venuspoint.externalid.isAccepted(spec.externalid);
		const validPassword = venuspoint.pwd.isAccepted(spec.pwd);
		return {
			...spec,
			validAccount,
			validPassword,
			validCurrency,
			validForSubmit: Boolean(validAccount && validPassword && validCurrency)
		};
	}
	throw new Error(`payment.js:validateTemplate: methodtype ${formTemplateCode} is unknown`);
}

function validateSpec() {
	return (dispatch, getState) => {
		const {
			status: { formTemplateCode, autoskip, blikCodeRequired, methodId },
			paymentMethods,
			lastUsedMethodDetails,
			methodDetails,
			spec
		} = getState().payment;
		const { paymentMethodIdsStr } = getState().bonuses;
		const firstDeposit = lastUsedMethodDetails && lastUsedMethodDetails.firstDeposit;
		if (paymentMethods[methodId]?.isApplePay) {
			console.info('reached apple pay'); // eslint-disable-line
			return dispatch({
				type: VALIDATE_SPEC,
				payload: {
					spec: { ...spec, validatedSpec: true, validCurrency: true },
					status: {
						autoskipEnabledAndValid: false,
						promptsecureid: null
					}
				}
			});
		}

		validateTemplate(formTemplateCode, spec, blikCodeRequired, methodDetails, paymentMethods).then(
			specValidated => {
				dispatch({
					type: VALIDATE_SPEC,
					payload: {
						spec: { validatedSpec: false },
						status: { autoskipEnabledAndValid: false }
					}
				});

				const { validAccount } = specValidated || {};

				const getAutoSkip = () => {
					switch (true) {
						case formTemplateCode === 'NO-FIELDS' && !firstDeposit:
							return true;
						case formTemplateCode === 'SWISH' && !firstDeposit:
							return true;
						case (formTemplateCode === 'BANK_WIRE_INDIA' || formTemplateCode === 'BANK_WIRE_INDIA_UPI') &&
							!firstDeposit:
							return true;
						case formTemplateCode === 'FOGHORN_DEPOSIT' && !firstDeposit:
							return true;
						case validAccount && autoskip:
							return true;
						default:
							return false;
					}
				};
				const isMethodAllowed = allowsMethod(methodId, paymentMethodIdsStr);
				const autoskipEnabledAndValid = isMethodAllowed && getAutoSkip();

				return dispatch({
					type: VALIDATE_SPEC,
					payload: {
						spec: { ...specValidated, validatedSpec: true },
						status: { autoskipEnabledAndValid }
					}
				});
			}
		);
	};
}

async function isKycNeeded(paymentMethodId, providerId, cid) {
	try {
		const urlParams = createUrlParameters({
			paymentMethodId,
			...(providerId && { providerId }),
			...(cid && { cid })
		});

		const response = await requestApi(`/KycService/public/getValidateKYCStatus/forDeposits?${urlParams}`);
		return response;
	} catch (err) {
		throw new Error(err.message);
	}
}

export {
	setLimits,
	selectProduct,
	getFeesAndLimits,
	generalError,
	validateSpec,
	validateTemplate,
	updateExecutePrecheck,
	cancelPrompts,
	showPrompts,
	wrappedRequestApi,
	createBonusParams,
	getPAGdata,
	getBrowserInfo,
	getCreditCardEncryptionKey,
	encryptCreditCardNumber,
	isKycNeeded
};
